/**
 * Pure helpers for the master community-chat discussion list. Kept RN-free so
 * the correctness-critical filtering (CORE-359) is unit-testable in a node jest
 * env without dragging in stream-chat-react / react-native.
 */
import type { ChatDiscussion, ChatDiscussionDetails } from '../../types/auth';

/**
 * The general community chat is the channel's top-level timeline, not a card
 * message. It's identified by the reserved id "general" or a general/community
 * type — never dropped from the list.
 */
export function isGeneral(
  discussion: ChatDiscussion | ChatDiscussionDetails | null,
  selectedId: string | null,
): boolean {
  const id = (discussion?.id ?? selectedId ?? '').toLowerCase();
  const type = (discussion?.type ?? '').toLowerCase();
  return id === 'general' || type === 'general' || type === 'community';
}

/**
 * CORE-359 — drop event discussions whose backing Stream card message is gone.
 *
 * The BE lists any event (live / watch-party / master-event) that carries a
 * stored `StreamMessageId`, but if that message no longer exists in Stream the
 * row opens to a dead "Обсуждение недоступно." screen and reads as broken data.
 * `missingEventIds` is the set of ids the panel has confirmed are gone; we drop
 * those rows. The general chat is never a card message, so it always stays.
 */
export function filterAvailableDiscussions(
  discussions: ChatDiscussion[],
  missingEventIds: ReadonlySet<string>,
): ChatDiscussion[] {
  if (missingEventIds.size === 0) return discussions;
  return discussions.filter((d) => isGeneral(d, d.id) || !missingEventIds.has(d.id));
}

/**
 * CORE-577 — the community-chat endpoints (`/chat/masters/{id}/discussions`,
 * `/online-users`, `/MasterProfile/{id}/circle`) resolve a master profile only
 * when its status is Published, and answer 404 otherwise. For the owning master
 * on their own dashboard that 404 isn't "not found" — it means their profile
 * isn't published yet, and the generic «Не нашли то, что искали» reads as a
 * breakage. Only the owner gets the explanation: for anyone else an
 * unpublished profile must stay indistinguishable from a missing one.
 */
export function isOwnProfileUnpublished(err: unknown, isOwner: boolean): boolean {
  if (!isOwner) return false;
  return (err as { status?: number } | null)?.status === 404;
}

/**
 * Resolve which discussion a live Stream `message.new` event belongs to, for
 * the left-column counters. Returns `'general'` for a top-level community
 * message, the parent message id for a thread reply (event discussions are
 * threads under their card message), or `null` when the event should not bump
 * any counter: another channel, a system row, a deleted message, or a new
 * event *card* (top-level message stamped with an event marker — it opens a
 * discussion, it isn't a message inside one).
 */
export function liveMessageDiscussionId(
  event: { cid?: string; message?: any } | null | undefined,
  masterProfileId: number,
): string | null {
  if (event?.cid !== `messaging:master-circle-${masterProfileId}`) return null;
  const m = event.message;
  if (!m || m.type === 'system' || m.deleted_at) return null;
  if (m.parent_id) return m.parent_id;
  if (m.eventId != null || m.event_id != null) return null;
  return 'general';
}

// ─── CORE-1655 — тема в общей ленте и смена открытого обсуждения ───
// Правила действий, порядок списка и форма темы живут в
// ./discussionManagement (CORE-1654): здесь только то, чего там нет.

/** Карточка темы в ленте общего чата: сообщение-корень с `eventKind: topic`. */
export function readTopicCard(raw: any): { discussionId: string; title: string; description: string; authorName: string } | null {
  const custom = raw?.custom ?? raw;
  if (!custom || (custom.eventKind ?? custom.event_kind) !== 'topic') return null;
  const discussionId = raw?.externalStreamId ?? raw?.ExternalStreamId ?? null;
  if (!discussionId) return null;
  return {
    discussionId: String(discussionId),
    title: String(custom.title ?? 'Тема'),
    description: String(custom.description ?? ''),
    authorName: String(custom.authorName ?? ''),
  };
}

/**
 * CORE-1655 — какое обсуждение держать открытым после того, как список
 * поменял другой участник.
 *
 * Открытое обсуждение было в списке и пропало — его удалили: уводим в общий
 * чат, иначе человек остаётся в пустой ленте. Обсуждение, которого в списке и
 * не было (открыто по прямой ссылке), не трогаем: его отсутствие в новом
 * списке ничего не значит.
 */
export function discussionAfterListChange(
  selectedId: string | null,
  before: ReadonlyArray<Pick<ChatDiscussion, 'id'>>,
  after: ReadonlyArray<Pick<ChatDiscussion, 'id' | 'type'>>,
): string | null {
  if (!selectedId) return selectedId;
  const wasListed = before.some((d) => d.id === selectedId);
  const stillListed = after.some((d) => d.id === selectedId);
  if (!wasListed || stillListed) return selectedId;
  return after.find((d) => isGeneral(d as ChatDiscussion, d.id))?.id ?? 'general';
}
